import React, { useState, useEffect, useImperativeHandle, forwardRef, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import { getThreadList, deleteThread, updateThreadName } from "../../services/apiService";
import FullScreenLoader from "../Common/FullScreenLoader";

const ChatTabs = forwardRef(({
  activeTab,
  chatContexts,
  onTabChange,
  onCloseTab,
  onCreateNewTab,
  onEditTab,
  onDeleteTab,
  onEditDefaultTab,
  defaultTabName = "Default Chat",
  truncateTabName,
  selectedTabDetails,
  isLoading = false,
  sidebarOpen = true,
}, ref) => {
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedTabForMenu, setSelectedTabForMenu] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editTabName, setEditTabName] = useState("");
  const [editingTabId, setEditingTabId] = useState(null);
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [hoveredTab, setHoveredTab] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const isFetchingThreadsRef = useRef(false);
  const autoSelectedThreadRef = useRef(null);
  const [userInfo, setUserInfo] = useState({
    accessToken: '',
    userId: '',
    email: '',
    full_name: '',
    persona: '',
  });
  useEffect(() => {
    // Listen for personaSet event to update user info
    const handlePersonaSet = (event) => {
      if (event.detail) {
        const userInfoData = {
          accessToken: event.detail.accessToken || '',
          userId: event.detail.userId || '',
          email: event.detail.email || '',
          full_name: event.detail.full_name || '',
          persona: event.detail.persona || '',
        };

        setUserInfo(userInfoData);
      }
    };

    // Check for existing user data on component mount
    const checkExistingUserData = () => {
      const isAuth = localStorage.getItem('authenticated') === 'true';
      const accessToken = localStorage.getItem('accessToken');
      const userId = localStorage.getItem('userId');
      const email = localStorage.getItem('userEmail');
      const fullName = localStorage.getItem('userFullName');
      const persona = localStorage.getItem('persona');

      if (isAuth && accessToken && userId && email && fullName) {
        const userInfoData = {
          accessToken: accessToken,
          userId: userId,
          email: email,
          full_name: fullName,
          persona: persona || 'default'
        };
        setUserInfo(userInfoData);
      }
    };

    window.addEventListener('personaSet', handlePersonaSet);

    // Check for existing data on mount
    checkExistingUserData();

    return () => {
      window.removeEventListener('personaSet', handlePersonaSet);
    };
  }, []);

  useEffect(() => {
    isFetchingThreadsRef.current = false;
    autoSelectedThreadRef.current = null;
    setHasInitialized(false);
  }, [userInfo.userId]);

  // Fetch threads on component mount - only after successful login
  const fetchThreads = useCallback(async (skipLoader = false) => {
    // Only fetch threads if user is logged in and has a valid userId
    if (!userInfo.userId || !userInfo.accessToken) {
      setLoading(false);
      return;
    }

    if (isFetchingThreadsRef.current) {
      return;
    }

    isFetchingThreadsRef.current = true;

    try {
      if (!skipLoader) setLoading(true);
      const response = await getThreadList(userInfo.userId);

      const fetchedThreads = response?.chat_groups || [];
      setThreads(fetchedThreads);
      setHasInitialized(true); // Mark as initialized after first successful fetch

      // Auto-select first thread group if available and no active tab is set
      // This will trigger chat history loading in MainPage component
      if (
        fetchedThreads.length > 0 &&
        (!activeTab || activeTab === 'default') &&
        autoSelectedThreadRef.current !== fetchedThreads[0].thread_id
      ) {
        autoSelectedThreadRef.current = fetchedThreads[0].thread_id;
        // Use setTimeout to ensure the component is fully rendered before triggering tab change
        setTimeout(() => {
          onTabChange(null, fetchedThreads[0].thread_id);
        }, 100);
      }
    } catch (error) {
      console.error('Error fetching threads:', error);
      setThreads([]);
      setHasInitialized(true); // Mark as initialized even on error
    } finally {
      isFetchingThreadsRef.current = false;
      if (!skipLoader) setLoading(false);
    }
  }, [activeTab, onTabChange, userInfo.accessToken, userInfo.userId]);

  // Expose fetchThreads function to parent component
  useImperativeHandle(ref, () => ({
    fetchThreads: (skipLoader = false) => fetchThreads(skipLoader)
  }), [fetchThreads]);

  useEffect(() => {
    // Only fetch if not yet initialized and valid credentials are present
    if (!hasInitialized && userInfo.userId && userInfo.accessToken) {
      fetchThreads();
    }
  }, [fetchThreads, hasInitialized, userInfo.userId, userInfo.accessToken]);

  const handleMenuOpen = (event, contextId) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setSelectedTabForMenu(contextId);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedTabForMenu(null);
  };

  const handleEditClick = () => {
    if (selectedTabForMenu === 'default') {
      setEditTabName(defaultTabName);
      setEditingTabId('default');
      setEditDialogOpen(true);
    } else {
      // First check if it's a thread from API
      const thread = threads.find(t => t.thread_id === selectedTabForMenu);
      if (thread) {
        setEditTabName(thread.thread_name || `Thread ${thread.thread_id}`);
        setEditingTabId(selectedTabForMenu);
        setEditDialogOpen(true);
      } else {
        // Then check if it's a chat context
        const context = chatContexts.find(ctx => ctx.id === selectedTabForMenu);
        if (context) {
          setEditTabName(context.name);
          setEditingTabId(selectedTabForMenu);
          setEditDialogOpen(true);
        }
      }
    }
    handleMenuClose();
  };

  // Helper function to check if tab can be deleted
  const canDeleteTab = (tabId) => {
    if (!tabId || tabId === 'default') return false;
    const isFirstThread = threads.length > 0 && threads[0].thread_id === tabId;
    return !isFirstThread;
  };

  // Helper function to delete API thread
  const deleteApiThread = async (tabId) => {
    try {
      setLoading(true);
      const response = await deleteThread(userInfo.userId, tabId);

      if (response && !response.error) {
        const threadsResponse = await getThreadList(userInfo.userId);
        const fetchedThreads = threadsResponse?.chat_groups || [];
        setThreads(fetchedThreads);
      } else {
        console.error("Failed to delete thread:", response?.message);
      }
    } catch (error) {
      console.error("Error refreshing threads after delete:", error);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to handle active tab switching
  const switchActiveTabIfNeeded = (deletedTabId, wasActiveTab) => {
    if (wasActiveTab && threads.length > 0) {
      onTabChange(null, threads[0].thread_id);
    }
  };

  const handleDeleteClick = async () => {
    setLoading(false);

    if (!canDeleteTab(selectedTabForMenu)) {
      handleMenuClose();
      return;
    }

    const isActiveTab = selectedTabForMenu === activeTab;
    const isApiThread = threads.find(t => t.thread_id === selectedTabForMenu);

    if (isApiThread) {
      await deleteApiThread(selectedTabForMenu);
    } else {
      onDeleteTab(selectedTabForMenu);
    }

    switchActiveTabIfNeeded(selectedTabForMenu, isActiveTab);
    handleMenuClose();
  };

  const handleEditSave = async () => {
    if (editingTabId && editTabName.trim()) {
      if (editingTabId === 'default') {
        // Handle default tab name update
        if (onEditDefaultTab) {
          onEditDefaultTab(editTabName.trim());
          handleEdit(userInfo.userId, editingTabId, editTabName.trim());
        }
      } else {
        // Check if it's a thread from API that needs server update
        const thread = threads.find(t => t.thread_id === editingTabId);
        if (thread) {
          handleEdit(userInfo.userId, editingTabId, editTabName.trim());
        } else {
          // For local chat contexts, use the existing edit handler
          onEditTab(editingTabId, editTabName.trim());
        }
      }
      setEditDialogOpen(false);
      setEditTabName("");
      setEditingTabId(null);
    }
  };

  const handleEdit = async (userId, threadId, newName) => {
    try {
      const response = await updateThreadName(userId, threadId, newName);

      if (response && !response.error) {
        // console.log("Thread name updated successfully:", response);
        // Refresh threads to reflect the name change
        await fetchThreads(false); // Skip loader when refreshing
      } else {
        console.error("Failed to update thread name:", response?.message);
      }
    } catch (error) {
      console.error("Error updating thread name:", error);
    }
  }

  const handleEditCancel = () => {
    setEditDialogOpen(false);
    setEditTabName("");
    setEditingTabId(null);
  };

  const handleAddNewTab = () => {
    // Prevent new tab creation when loading
    if (isLoading) return;

    // Generate incremental label for new tab
    const existingTabNumbers = allTabs
      .map(tab => {
        const match = tab.name?.match(/^Chat (\d+)$/);
        return match ? parseInt(match[1]) : 0;
      })
      .filter(num => num > 0);

    const nextNumber = existingTabNumbers.length > 0 ? Math.max(...existingTabNumbers) + 1 : 1;
    const newTabName = `Chat ${nextNumber}`;
    onCreateNewTab(newTabName);
  };

  const isDefaultTab = selectedTabForMenu === 'default';
  const isFirstSavedThread = threads.length > 0 && threads[0].thread_id === selectedTabForMenu;

  // Combine chatContexts with threads from API - show newest first (desc order by creation date)
  const newTabCreationList = chatContexts.reverse()
  const allTabs = [
    ...newTabCreationList, // New UI-created tabs first (newest) - reverse to show Chat 3, Chat 2, Chat 1
    ...threads.map(thread => ({
      id: thread.thread_id,
      name: thread.thread_name || `Thread ${thread.thread_id}`
    })) // .reverse() Reverse threads to show newer API threads first
  ];
  // console.log("allTabs:", allTabs,newTabCreationList);
  // Check if user is logged in and has no saved thread groups to show Default Chat tab
  // Only show default tab if we have initialized and there are no threads
  const showDefaultTab = userInfo.userId && userInfo.accessToken && hasInitialized && (!loading && (!threads || threads.length === 0));

  // Filter tabs by search query
  const lowerQuery = searchQuery.toLowerCase();
  const filteredTabs = searchQuery
    ? allTabs.filter(t => t.name?.toLowerCase().includes(lowerQuery))
    : allTabs;
  const showDefaultInResults = showDefaultTab &&
    (!searchQuery || defaultTabName.toLowerCase().includes(lowerQuery));

  // console.log ("allTabs, showDefaultTab, hasInitialized:", allTabs, showDefaultTab, hasInitialized);
  // // Console log active tab information
  // useEffect(() => {
  //   if (activeTab) {
  //     if (activeTab === 'default') {
  //       console.log('Active Tab:', { id: 'default', name: defaultTabName });
  //     } else {
  //       // Find the active tab in allTabs
  //       const activeTabInfo = allTabs.find(tab => tab.id === activeTab);
  //       if (activeTabInfo) {
  //         console.log('Active Tab1111:', { id: activeTabInfo.id, name: activeTabInfo.name });

  //       } else {
  //         console.log('Active Tab:', { id: activeTab, name: 'Unknown' });
  //       }
  //     }
  //   }
  // }, [activeTab, allTabs, defaultTabName]);

  return (
    <>
      <FullScreenLoader
        loading={loading}
        message="Loading..."
        size={36}
      />

      {sidebarOpen && (userInfo.userId && userInfo.accessToken) && (
        <>
          {/* New Chat button */}
          <Box sx={{ px: 1.5, pt: 1, pb: 0.5 }}>
            <Button
              fullWidth
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={handleAddNewTab}
              disabled={isLoading}
              sx={{
                textTransform: 'none',
                borderRadius: '8px',
                fontFamily: "'Poppins', sans-serif",
                fontSize: '0.8rem',
                fontWeight: 500,
                borderColor: '#e2e8f0',
                color: '#2D3748',
                justifyContent: 'flex-start',
                '&:hover': { borderColor: '#1B2559', color: '#1B2559', backgroundColor: '#EEF2FF' },
                '&.Mui-disabled': { opacity: 0.5 },
              }}
            >
              New Chat
            </Button>
          </Box>

          {/* Recents header + search */}
          {(showDefaultTab || allTabs.length > 0) && (
            <Box sx={{ px: 1.5, pt: 1.5, pb: 0.5 }}>
              {searchOpen ? (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    backgroundColor: '#F5F7FA',
                    borderRadius: '8px',
                    px: 1,
                    py: 0.25,
                    border: '1px solid #e2e8f0',
                  }}
                >
                  <SearchIcon sx={{ fontSize: '14px', color: '#718096', mr: 0.5, flexShrink: 0 }} />
                  <input
                    autoFocus
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search chats..."
                    style={{
                      border: 'none',
                      outline: 'none',
                      background: 'transparent',
                      fontSize: '0.8rem',
                      fontFamily: "'Poppins', sans-serif",
                      color: '#2D3748',
                      flexGrow: 1,
                      width: '100%',
                      padding: '2px 0',
                    }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => { setSearchOpen(false); setSearchQuery(''); }}
                    sx={{ p: 0.25, ml: 0.25, color: '#718096', '&:hover': { color: '#1B2559' } }}
                  >
                    <CloseIcon sx={{ fontSize: '14px' }} />
                  </IconButton>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography
                    sx={{
                      fontSize: '0.7rem',
                      color: '#718096',
                      fontWeight: 600,
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                      fontFamily: "'Poppins', sans-serif",
                    }}
                  >
                    Recents
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => setSearchOpen(true)}
                    sx={{ p: 0.25, color: '#718096', '&:hover': { color: '#1B2559' } }}
                  >
                    <SearchIcon sx={{ fontSize: '16px' }} />
                  </IconButton>
                </Box>
              )}
            </Box>
          )}

          {/* Thread list */}
          <List disablePadding>
            {/* Default Chat item */}
            {showDefaultInResults && (
              <ListItem disablePadding sx={{ px: 1, py: 0.25 }}>
                <Tooltip title={defaultTabName} placement="right" arrow enterDelay={600}>
                  <ListItemButton
                    onClick={() => onTabChange(null, 'default')}
                  onMouseEnter={() => setHoveredTab('default')}
                  onMouseLeave={() => setHoveredTab(null)}
                  sx={{
                    borderRadius: '8px',
                    py: 0.75,
                    px: 1.5,
                    backgroundColor: activeTab === 'default' ? '#EEF2FF' : 'transparent',
                    '&:hover': { backgroundColor: activeTab === 'default' ? '#E0E7FF' : '#F5F7FA' },
                    '& .MuiTouchRipple-root': { display: 'none' },
                  }}
                >
                  <ListItemText
                    primary={defaultTabName}
                    sx={{
                      minWidth: 0,
                      '& .MuiListItemText-primary': {
                        fontFamily: "'Poppins', sans-serif",
                        fontSize: '0.875rem',
                        fontWeight: activeTab === 'default' ? 600 : 400,
                        color: activeTab === 'default' ? '#1B2559' : '#2D3748',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      },
                    }}
                  />
                  <IconButton
                    size="small"
                    onClick={(e) => { e.stopPropagation(); handleMenuOpen(e, 'default'); }}
                    sx={{
                      p: 0.25,
                      ml: 0.5,
                      flexShrink: 0,
                      color: '#718096',
                      visibility: (hoveredTab === 'default' || activeTab === 'default') ? 'visible' : 'hidden',
                      '&:hover': { color: '#1B2559' },
                    }}
                  >
                    <MoreVertIcon sx={{ fontSize: '16px' }} />
                  </IconButton>
                  </ListItemButton>
                </Tooltip>
              </ListItem>
            )}

            {/* API thread & local context items */}
            {filteredTabs.map((context) => (
              <ListItem key={context.id} disablePadding sx={{ px: 1, py: 0.25 }}>
                <Tooltip title={context.name} placement="right" arrow enterDelay={600}>
                  <ListItemButton
                    onClick={() => onTabChange(null, context.id)}
                  disabled={isLoading && activeTab !== context.id}
                  onMouseEnter={() => setHoveredTab(context.id)}
                  onMouseLeave={() => setHoveredTab(null)}
                  sx={{
                    borderRadius: '8px',
                    py: 0.75,
                    px: 1.5,
                    backgroundColor: activeTab === context.id ? '#EEF2FF' : 'transparent',
                    '&:hover': { backgroundColor: activeTab === context.id ? '#E0E7FF' : '#F5F7FA' },
                    '&.Mui-disabled': { opacity: 0.5 },
                    '& .MuiTouchRipple-root': { display: 'none' },
                  }}
                >
                  <ListItemText
                    primary={context.name}
                    sx={{
                      minWidth: 0,
                      '& .MuiListItemText-primary': {
                        fontFamily: "'Poppins', sans-serif",
                        fontSize: '0.875rem',
                        fontWeight: activeTab === context.id ? 600 : 400,
                        color: activeTab === context.id ? '#1B2559' : '#2D3748',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      },
                    }}
                  />
                  <IconButton
                    size="small"
                    onClick={(e) => { e.stopPropagation(); handleMenuOpen(e, context.id); }}
                    sx={{
                      p: 0.25,
                      ml: 0.5,
                      flexShrink: 0,
                      color: '#718096',
                      visibility: (hoveredTab === context.id || activeTab === context.id) ? 'visible' : 'hidden',
                      opacity: (isLoading && activeTab !== context.id) ? 0.5 : 1,
                      pointerEvents: (isLoading && activeTab !== context.id) ? 'none' : 'auto',
                      '&:hover': { color: '#1B2559' },
                    }}
                  >
                    <MoreVertIcon sx={{ fontSize: '16px' }} />
                  </IconButton>
                  </ListItemButton>
                </Tooltip>
              </ListItem>
            ))}
          </List>
        </>
      )}

      {/* Context Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          elevation: 3,
          sx: {
            minWidth: 120,
            '& .MuiMenuItem-root': {
              fontSize: '0.875rem',
              minHeight: '36px',
              paddingY: '6px',
              paddingX: '12px',
            },
            '& .MuiListItemText-primary': {
              fontSize: '0.875rem',
              fontWeight: 400,
            },
            '& .MuiListItemIcon-root': {
              minWidth: '24px',
              marginRight: '8px',
            },
          },
        }}
      >
        <MenuItem onClick={handleEditClick}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Rename"
            primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 400 }}
          />
        </MenuItem>
        <MenuItem
          onClick={handleDeleteClick}
          disabled={isDefaultTab || isFirstSavedThread}
          sx={{ '&.Mui-disabled': { opacity: 0.5 } }}
        >
          <ListItemIcon>
            <DeleteIcon
              fontSize="small"
              sx={{ color: (isDefaultTab || isFirstSavedThread) ? 'rgba(0, 0, 0, 0.26)' : 'inherit' }}
            />
          </ListItemIcon>
          <ListItemText
            primary="Delete"
            primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 400 }}
          />
        </MenuItem>
      </Menu>

      {/* Edit Tab Name Dialog */}
      <Dialog open={editDialogOpen} onClose={handleEditCancel} maxWidth="sm" fullWidth>
        <DialogTitle>Rename Context Name</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Context Name"
            type="text"
            fullWidth
            variant="outlined"
            value={editTabName}
            onChange={(e) => setEditTabName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleEditSave(); }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditCancel}>Cancel</Button>
          <Button onClick={handleEditSave} variant="contained" disabled={!editTabName.trim()}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
});

ChatTabs.propTypes = {
  activeTab: PropTypes.string,
  chatContexts: PropTypes.array,
  onTabChange: PropTypes.func,
  onCloseTab: PropTypes.func,
  onCreateNewTab: PropTypes.func,
  onEditTab: PropTypes.func,
  onDeleteTab: PropTypes.func,
  onEditDefaultTab: PropTypes.func,
  defaultTabName: PropTypes.string,
  truncateTabName: PropTypes.func,
  selectedTabDetails: PropTypes.object,
  isLoading: PropTypes.bool,
  sidebarOpen: PropTypes.bool,
};

ChatTabs.defaultProps = {
  defaultTabName: "Default Chat",
  isLoading: false
};

export default ChatTabs;
